import { supabase } from '../lib/supabase';
import { format } from 'date-fns';

/**
 * Advanced Analytics Service
 * Provides comprehensive analytics data aggregation, drill-down capabilities,
 * date range comparisons, segment filtering, and custom report generation
 */
class AdvancedAnalyticsService {
  /**
   * Get aggregated analytics data for a date range with comparison
   * @param {Object} params - Query parameters
   * @param {Date} params.startDate - Start date for primary range
   * @param {Date} params.endDate - End date for primary range
   * @param {Date} params.compareStartDate - Start date for comparison range
   * @param {Date} params.compareEndDate - End date for comparison range
   * @param {Array} params.segments - Array of segment filters (role, artist_type, etc.)
   * @returns {Object} Aggregated analytics with comparison data
   */
  async getAnalyticsSummary({ 
    startDate, 
    endDate, 
    compareStartDate = null, 
    compareEndDate = null,
    segments = []
  }) {
    try {
      // Get primary period data
      const primaryData = await this._getMetricsForPeriod(startDate, endDate, segments);
      
      // Get comparison period data if dates provided
      let comparisonData = null;
      let percentageChanges = null;
      
      if (compareStartDate && compareEndDate) {
        comparisonData = await this._getMetricsForPeriod(compareStartDate, compareEndDate, segments);
        percentageChanges = this._calculatePercentageChanges(primaryData, comparisonData);
      }

      return {
        primary: primaryData,
        comparison: comparisonData,
        changes: percentageChanges,
        dateRange: {
          start: format(startDate, 'yyyy-MM-dd'),
          end: format(endDate, 'yyyy-MM-dd')
        },
        comparisonRange: compareStartDate ? {
          start: format(compareStartDate, 'yyyy-MM-dd'),
          end: format(compareEndDate, 'yyyy-MM-dd')
        } : null
      };
    } catch (error) {
      console.error('Error getting analytics summary:', error);
      throw error;
    }
  }

  /**
   * Internal method to get metrics for a specific period
   */
  async _getMetricsForPeriod(startDate, endDate, segments = []) {
    const formattedStart = format(startDate, 'yyyy-MM-dd');
    const formattedEnd = format(endDate, 'yyyy-MM-dd');

    // Build segment filter conditions
    const segmentConditions = this._buildSegmentConditions(segments);

    // Fetch user analytics
    let userAnalyticsQuery = supabase?.from('user_analytics')?.select('metric_type, metric_value, metric_date, user_id, user_profiles!inner(role, artist_type, status)')?.gte('metric_date', formattedStart)?.lte('metric_date', formattedEnd);

    // Apply segment filters to user analytics
    if (segmentConditions?.length > 0) {
      segmentConditions?.forEach(condition => {
        if (condition?.field?.startsWith('user_profiles.')) {
          const field = condition?.field?.replace('user_profiles.', '');
          userAnalyticsQuery = userAnalyticsQuery?.eq(`user_profiles.${field}`, condition?.value);
        }
      });
    }

    const { data: userAnalytics, error: userError } = await userAnalyticsQuery;
    if (userError) throw userError;

    // Fetch mobile app analytics
    let mobileAnalyticsQuery = supabase?.from('mobile_app_analytics')?.select('metric_type, metric_value, metric_date, artist_id, user_profiles!inner(role, artist_type, status)')?.gte('metric_date', formattedStart)?.lte('metric_date', formattedEnd);

    // Apply segment filters to mobile analytics
    if (segmentConditions?.length > 0) {
      segmentConditions?.forEach(condition => {
        if (condition?.field?.startsWith('user_profiles.')) {
          const field = condition?.field?.replace('user_profiles.', '');
          mobileAnalyticsQuery = mobileAnalyticsQuery?.eq(`user_profiles.${field}`, condition?.value);
        }
      });
    }

    const { data: mobileAnalytics, error: mobileError } = await mobileAnalyticsQuery;
    if (mobileError) throw mobileError;

    // Fetch user activity
    let activityQuery = supabase?.from('user_activity')?.select('activity_type, created_at, user_id, user_profiles!inner(role, artist_type, status)')?.gte('created_at', `${formattedStart}T00:00:00`)?.lte('created_at', `${formattedEnd}T23:59:59`);

    // Apply segment filters to activity
    if (segmentConditions?.length > 0) {
      segmentConditions?.forEach(condition => {
        if (condition?.field?.startsWith('user_profiles.')) {
          const field = condition?.field?.replace('user_profiles.', '');
          activityQuery = activityQuery?.eq(`user_profiles.${field}`, condition?.value);
        }
      });
    }

    const { data: userActivity, error: activityError } = await activityQuery;
    if (activityError) throw activityError;

    // Aggregate metrics
    return this._aggregateMetrics(userAnalytics, mobileAnalytics, userActivity);
  }

  /**
   * Build segment filter conditions
   */
  _buildSegmentConditions(segments) {
    const conditions = [];
    
    segments?.forEach(segment => {
      if (segment?.type === 'role' && segment?.value) {
        conditions?.push({ field: 'user_profiles.role', value: segment?.value });
      }
      if (segment?.type === 'artist_type' && segment?.value) {
        conditions?.push({ field: 'user_profiles.artist_type', value: segment?.value });
      }
      if (segment?.type === 'status' && segment?.value) {
        conditions?.push({ field: 'user_profiles.status', value: segment?.value });
      }
    });
    
    return conditions;
  }

  /**
   * Aggregate metrics from multiple data sources
   */
  _aggregateMetrics(userAnalytics, mobileAnalytics, userActivity) {
    const metrics = {
      totalUsers: new Set(),
      profileViews: 0,
      contentUploads: 0,
      appDownloads: 0,
      activeUsers: 0,
      pageClicks: 0,
      sessionDuration: 0,
      activities: {
        login: 0,
        profile_update: 0,
        content_upload: 0,
        registration: 0
      },
      byRole: {
        admin: 0,
        editor: 0,
        artist: 0
      },
      byArtistType: {
        musician: 0,
        visual: 0,
        director: 0,
        actor: 0,
        writer: 0
      },
      dailyMetrics: {}
    };

    // Process user analytics
    userAnalytics?.forEach(item => {
      metrics?.totalUsers?.add(item?.user_id);
      
      if (item?.metric_type === 'profile_views') {
        metrics.profileViews += item?.metric_value || 0;
      }
      if (item?.metric_type === 'content_uploads') {
        metrics.contentUploads += item?.metric_value || 0;
      }

      // Track by role
      const role = item?.user_profiles?.role;
      if (role && metrics?.byRole?.[role] !== undefined) {
        metrics.byRole[role]++;
      }

      // Track by artist type
      const artistType = item?.user_profiles?.artist_type;
      if (artistType && metrics?.byArtistType?.[artistType] !== undefined) {
        metrics.byArtistType[artistType]++;
      }

      // Track daily metrics
      const date = item?.metric_date;
      if (!metrics?.dailyMetrics?.[date]) {
        metrics.dailyMetrics[date] = {
          profileViews: 0,
          contentUploads: 0,
          appDownloads: 0,
          pageClicks: 0
        };
      }
      if (item?.metric_type === 'profile_views') {
        metrics.dailyMetrics[date].profileViews += item?.metric_value || 0;
      }
      if (item?.metric_type === 'content_uploads') {
        metrics.dailyMetrics[date].contentUploads += item?.metric_value || 0;
      }
    });

    // Process mobile analytics
    mobileAnalytics?.forEach(item => {
      if (item?.artist_id) {
        metrics?.totalUsers?.add(item?.artist_id);
      }

      if (item?.metric_type === 'app_downloads') {
        metrics.appDownloads += item?.metric_value || 0;
      }
      if (item?.metric_type === 'active_users') {
        metrics.activeUsers += item?.metric_value || 0;
      }
      if (item?.metric_type === 'page_clicks') {
        metrics.pageClicks += item?.metric_value || 0;
      }
      if (item?.metric_type === 'session_duration') {
        metrics.sessionDuration += item?.metric_value || 0;
      }

      // Track daily metrics
      const date = item?.metric_date;
      if (!metrics?.dailyMetrics?.[date]) {
        metrics.dailyMetrics[date] = {
          profileViews: 0,
          contentUploads: 0,
          appDownloads: 0,
          pageClicks: 0
        };
      }
      if (item?.metric_type === 'app_downloads') {
        metrics.dailyMetrics[date].appDownloads += item?.metric_value || 0;
      }
      if (item?.metric_type === 'page_clicks') {
        metrics.dailyMetrics[date].pageClicks += item?.metric_value || 0;
      }
    });

    // Process user activity
    userActivity?.forEach(item => {
      if (item?.user_id) {
        metrics?.totalUsers?.add(item?.user_id);
      }

      const activityType = item?.activity_type;
      if (metrics?.activities?.[activityType] !== undefined) {
        metrics.activities[activityType]++;
      }
    });

    // Convert Set to count
    metrics.totalUsers = metrics?.totalUsers?.size || 0;

    return metrics;
  }

  /**
   * Calculate percentage changes between two periods
   */
  _calculatePercentageChanges(current, previous) {
    const calculateChange = (currentVal, previousVal) => {
      if (previousVal === 0) return currentVal > 0 ? 100 : 0;
      return ((currentVal - previousVal) / previousVal) * 100;
    };

    return {
      totalUsers: calculateChange(current?.totalUsers, previous?.totalUsers),
      profileViews: calculateChange(current?.profileViews, previous?.profileViews),
      contentUploads: calculateChange(current?.contentUploads, previous?.contentUploads),
      appDownloads: calculateChange(current?.appDownloads, previous?.appDownloads),
      activeUsers: calculateChange(current?.activeUsers, previous?.activeUsers),
      pageClicks: calculateChange(current?.pageClicks, previous?.pageClicks),
      sessionDuration: calculateChange(current?.sessionDuration, previous?.sessionDuration)
    };
  }

  /**
   * Get drill-down data for a specific metric
   */
  async getDrillDownData(metricType, startDate, endDate, segments = []) {
    try {
      const formattedStart = format(startDate, 'yyyy-MM-dd');
      const formattedEnd = format(endDate, 'yyyy-MM-dd');
      const segmentConditions = this._buildSegmentConditions(segments);

      // Determine which table to query based on metric type
      const table = this._getTableForMetric(metricType);
      
      let query = supabase?.from(table)?.select('*, user_profiles!inner(full_name, email, role, artist_type, status)')?.gte('metric_date', formattedStart)?.lte('metric_date', formattedEnd);

      // Apply metric type filter if applicable
      if (table === 'user_analytics' || table === 'mobile_app_analytics') {
        query = query?.eq('metric_type', metricType);
      }

      // Apply segment filters
      if (segmentConditions?.length > 0) {
        segmentConditions?.forEach(condition => {
          if (condition?.field?.startsWith('user_profiles.')) {
            const field = condition?.field?.replace('user_profiles.', '');
            query = query?.eq(`user_profiles.${field}`, condition?.value);
          }
        });
      }

      const { data, error } = await query?.order('metric_date', { ascending: false });
      
      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting drill-down data:', error);
      throw error;
    }
  }

  /**
   * Get table name for a specific metric
   */
  _getTableForMetric(metricType) {
    const mobileMetrics = ['app_downloads', 'active_users', 'page_clicks', 'session_duration', 'geographic_data'];
    return mobileMetrics?.includes(metricType) ? 'mobile_app_analytics' : 'user_analytics';
  }

  /**
   * Get available segments for filtering
   */
  async getAvailableSegments() {
    try {
      const { data: profiles, error } = await supabase?.from('user_profiles')?.select('role, artist_type, status')?.not('role', 'is', null);

      if (error) throw error;

      const segments = {
        roles: [...new Set(profiles?.map(p => p?.role)?.filter(Boolean))],
        artistTypes: [...new Set(profiles?.map(p => p?.artist_type)?.filter(Boolean))],
        statuses: [...new Set(profiles?.map(p => p?.status)?.filter(Boolean))]
      };

      return segments;
    } catch (error) {
      console.error('Error getting available segments:', error);
      throw error;
    }
  }

  /**
   * Save custom report configuration
   */
  async saveReportTemplate(template) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const reportConfig = {
        ...template,
        created_by: user?.id,
        created_at: new Date()?.toISOString()
      };

      // Store in localStorage for now (could be moved to database)
      const savedReports = JSON?.parse(localStorage?.getItem('customReports') || '[]');
      savedReports?.push(reportConfig);
      localStorage?.setItem('customReports', JSON?.stringify(savedReports));

      return reportConfig;
    } catch (error) {
      console.error('Error saving report template:', error);
      throw error;
    }
  }

  /**
   * Get saved report templates
   */
  async getSavedReports() {
    try {
      const savedReports = JSON?.parse(localStorage?.getItem('customReports') || '[]');
      return savedReports;
    } catch (error) {
      console.error('Error getting saved reports:', error);
      return [];
    }
  }

  /**
   * Export analytics data
   */
  async exportData(format, data) {
    try {
      if (format === 'csv') {
        return this._exportToCSV(data);
      } else if (format === 'json') {
        return this._exportToJSON(data);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  }

  /**
   * Export data to CSV format
   */
  _exportToCSV(data) {
    const csvRows = [];
    const headers = Object?.keys(data?.[0] || {});
    csvRows?.push(headers?.join(','));

    data?.forEach(row => {
      const values = headers?.map(header => {
        const value = row?.[header];
        return typeof value === 'string' ? `"${value}"` : value;
      });
      csvRows?.push(values?.join(','));
    });

    return csvRows?.join('\n');
  }

  /**
   * Export data to JSON format
   */
  _exportToJSON(data) {
    return JSON?.stringify(data, null, 2);
  }
}

export default new AdvancedAnalyticsService();